import { computed, inject } from '@angular/core';
import { type AppState } from '@app/reducers/interfaces';
import { AbsenceService } from '@app/reducers/orm/absence/absence.service';
import { setError, setIdle, setLoading, withRequestStatus } from '@app/signal-store/with-request-status';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { plainDate, toISODateString } from '@shiftbase-com/ts-shared/utilities';
import { EMPTY, pipe, switchMap, tap } from 'rxjs';

import { PayrollRunService } from '../../payroll-run.service';
import { type PayrollRunScope } from '../../payroll.model';
import { emptyPayrollAbsenceGroups, type PayrollAbsenceGroups } from './payroll-absence-row';
import { selectPayrollAbsenceGroups } from './payroll-absences.selectors';

/**
 * The payroll run's absences, grouped into the step's three sections.
 *
 * A signal store rather than a plain signals service — unlike `PayrollRunService` and
 * `PayrollProgressService`, this owns a backend call and a request status. It holds no entities
 * of its own: `AbsenceService.load` populates the legacy store, and `groups` derives from it.
 *
 * The load deliberately sends no `status`, so one request feeds all three sections — the same
 * call shape `absence-overview.component.ts` uses for the yearly overview, which likewise renders
 * every state.
 *
 * `hasLoaded` exists for the step source, not for the UI. `deriveStepViews` reads
 * `outstanding() === 0` as 'complete', so a count taken from a store that has not been filled
 * yet would report the step done and let the page's auto-advance skip past it. The source stays
 * absent until this flips — see `payroll-sources.ts`.
 *
 * It tracks the CURRENT scope's confirmed data, not "ever loaded once": every new emission from
 * `run.scope` — including a mid-flow scope change — revokes it before the new load even settles,
 * so a stale `true` left over from the previous scope can never be read as "this scope is done".
 * It is left false on both the null-scope branch and the error branch.
 */
export const PayrollAbsencesStore = signalStore(
  { providedIn: 'root' },
  withRequestStatus(),
  withState<{ hasLoaded: boolean }>({ hasLoaded: false }),
  withComputed((store, ngrxStore = inject<Store<AppState>>(Store), run = inject(PayrollRunService)) => {
    // One selector instance per scope, deliberately split across two computeds.
    //
    // Building it inside the computed that *reads* it would rebuild it far more often than the
    // scope changes: reading the selector makes store state a dependency, so that computed re-runs
    // on every store change, and `selectPayrollAbsenceGroups(scope)` is a factory — each call is a
    // fresh `createSelector` with an empty cache. The grouping (period filter, a permission check
    // per absence, then the org-chart lookups) would then recompute from scratch on unrelated store
    // activity, which is exactly the work the memoization exists to avoid.
    //
    // This outer computed depends on `run.scope()` alone, so the instance is rebuilt only when the
    // run's scope genuinely changes. Discarding the previous one leaks nothing: `selectSignal` is
    // itself just a `computed` over state and creates no subscription.
    const groupsForScope = computed(() => {
      const scope = run.scope();

      return scope ? ngrxStore.selectSignal(selectPayrollAbsenceGroups(scope)) : null;
    });

    const groups = computed<PayrollAbsenceGroups>(() => groupsForScope()?.() ?? emptyPayrollAbsenceGroups());

    return {
      groups,
      // Only pending requests are work the period is waiting on. Reviewed ones stay on screen in
      // their own sections but never hold the step open.
      pendingCount: computed(() => groups().PENDING.length),
    };
  }),
  withMethods((store, absenceService = inject(AbsenceService)) => ({
    load: rxMethod<PayrollRunScope | null>(
      pipe(
        // `hasLoaded` describes the CURRENT scope's confirmed data, not "ever loaded once" — a
        // scope change must revoke it immediately, before the new load even settles, so a stale
        // `true` from the previous scope can never be read as "this scope is done".
        tap(() => patchState(store, { hasLoaded: false }, setLoading())),
        switchMap((scope) => {
          if (!scope) {
            patchState(store, setIdle());

            return EMPTY;
          }

          return absenceService
            .load({
              minDate: plainDate(toISODateString(scope.range.start)),
              maxDate: plainDate(toISODateString(scope.range.end)),
            })
            .pipe(
              tapResponse({
                next: () => patchState(store, { hasLoaded: true }, setIdle()),
                error: () => patchState(store, setError()),
              }),
            );
        }),
      ),
    ),
  })),
  withHooks({
    onInit(store, run = inject(PayrollRunService)) {
      // Passing the signal makes the load re-run on every scope change, including the reset to
      // null when the manager leaves the flow.
      store.load(run.scope);
    },
  }),
);
